/**
 * wake-lock.js — window.__wakeLock: keep the screen on, everywhere it can be done.
 *
 * Screen Wake Lock API landed late and unevenly: Chrome 84 (2020), Safari 16.4
 * (2023), Firefox 126 (2024). Anything older — and every browser that ships the
 * API but refuses the request — leaves a tool like /gps-speedometer with a
 * screen that dies at 100 km/h.
 *
 * The fallback is the long-standing NoSleep trick: play a tiny silent looping
 * video. Playback counts as "user is watching something" to the OS idle timer,
 * so the display stays lit. It costs a decoder and a few hundred bytes.
 *
 * Order matters: try the real API first (cheap, honest, no decoder), fall back
 * to video only when the API is absent or rejects.
 *
 * ── HOLD · VERIFY · REPAIR · REPORT (v2, 2026-08-19) ────────────────────────
 *
 * v1 asked "do I have a sentinel object?" and treated the answer as proof the
 * screen was staying on. It is not proof, and the gap was measured, not
 * guessed — see docs/wake-lock-failure-modes.md for the harness and results:
 *
 *   - A sentinel the OS already dropped is still a truthy object. After a
 *     freeze/bfcache restore the `release` event is never delivered, so
 *     `lock` stays non-null, every `if (!lock)` guard reads "still held", and
 *     nothing re-acquires. The screen dies while the UI says it is on. This is
 *     the shape iOS home-screen web apps hit constantly.
 *   - `'wakeLock' in navigator` can be true while `request` is missing; calling
 *     it throws SYNCHRONOUSLY, outside any promise, so `.catch()` never runs.
 *   - A resolved promise is not a held lock: WebKit resolves it on ports with
 *     no screen at all, and iOS may refuse to honour it in Low Power Mode.
 *   - `video.play()` rejecting is not observed by callers that ignore it, so
 *     "video fallback active" gets reported for a video that never started.
 *
 * The answer is not a list of per-bug workarounds — it is a control loop. We
 * never trust an acquisition; we re-derive `held()` from observable state
 * (`sentinel.released`, `video.paused`/`readyState`/`currentTime` advancing),
 * a watchdog re-checks it on a timer, and every lifecycle signal that can drop
 * a lock (visibilitychange, pageshow, focus after a hide, resume) re-arms it.
 * An unknown future failure mode that drops the lock is therefore repaired
 * without anyone knowing why it happened.
 *
 * Per spec a hidden document's screen lock is released, so after ANY hidden
 * period the old sentinel is dead by definition — we drop it on return rather
 * than wait for an event that bfcache swallows.
 *
 * Usage:
 *   __wakeLock.request().then(function (how) { ... })   // 'api' | 'video' | 'none' | 'deferred'
 *     ('deferred' — asked while the page was hidden; taken on the way back)
 *   __wakeLock.release()
 *   __wakeLock.active()   // 'api' | 'video' | null — VERIFIED, not intended
 *   __wakeLock.state()    // {mode, held, drops, repairs, escalated, lastError}
 *   __wakeLock.onChange(fn)
 *
 * Safari needs a user gesture to take the lock, so `request()` must be called
 * from a click. Repairs the watchdog makes later inherit the page's activation
 * and do not need a fresh one — but a page that RELOADS loses it, and the
 * caller has to ask for a tap again.
 *
 * This exists because ~68 call sites each hand-rolled their own copy of this
 * logic, all sharing the same has-an-object-so-it-must-be-held guard. New
 * tools use this instead of a 69th private copy — WakeLockPrimitiveTest keeps
 * the legacy list shrinking.
 */
(function () {
    'use strict';
    if (window.__wakeLock) return;

    // Two encodings of the same 0.4 s silent black clip, generated with ffmpeg
    // (VP8/WebM 557 B, H.264/MP4 1.5 KB) and inlined so the fallback needs no
    // network — the moment it is needed is the moment the screen is about to die.
    //
    // WebM is FIRST and it is not a nicety: Chromium and Firefox builds without
    // proprietary codecs (every Playwright build, Chromium on some Linux distros,
    // de-branded Android WebViews) cannot decode H.264 at all — the MP4-only
    // fallback every other tool copies fails there with MEDIA_ERR_SRC_NOT_SUPPORTED
    // and the screen sleeps anyway. Safari is the mirror case: no VP8 before
    // iOS 12.4-ish, but H.264 always. Ship both, let the engine choose.
    // (Measured 2026-08-19: the MP4-only copy in /screen-awake failed to decode
    // in all three Playwright engines while the tool displayed "video fallback
    // active" — canPlayType said "probably" for both codecs in the same run, so
    // it cannot be used to pick either.)
    var SRC_WEBM = 'data:video/webm;base64,GkXfo59ChoEBQveBAULygQRC84EIQoKEd2VibUKHgQJChYECGFOAZwEAAAAAAAH9EU2bdLpNu4tTq4QVSalmU6yBoU27i1OrhBZUrmtTrIHWTbuMU6uEElTDZ1OsggEcTbuMU6uEHFO7a1OsggHn7AEAAAAAAABZAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAVSalmsCrXsYMPQkBNgIxMYXZmNjEuMS4xMDBXQYxMYXZmNjEuMS4xMDBEiYhAeQAAAAAAABZUrmvBrgEAAAAAAAA414EBc8WI4PtkGk7ZvK+cgQAitZyDdW5kiIEAhoVWX1ZQOIOBASPjg4QL68IA4ImwgUC6gUCagQISVMNn+nNzn2PAgGfImUWjh0VOQ09ERVJEh4xMYXZmNjEuMS4xMDBzc9VjwItjxYjg+2QaTtm8r2fIoEWjh0VOQ09ERVJEh5NMYXZjNjEuMy4xMDAgbGlidnB4Z8ihRaOIRFVSQVRJT05Eh5MwMDowMDowMC40MDAwMDAwMDAAH0O2dcfngQCjqoEAAIDwAgCdASpAAEAAAEcIhYWIhYSIAgIABnA8QmAKsiD3MAD+/6tQgKOWgQDIANEBAA8R/AAYABhYL/QACI6AABxTu2uRu4+zgQC3iveBAfGCAZvwgQM=';
    var SRC_MP4  = 'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAMqbW9vdgAAAGxtdmhkAAAAAAAAAAAAAAAAAAAD6AAAAZAAAQAAAQAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgAAAlV0cmFrAAAAXHRraGQAAAADAAAAAAAAAAAAAAABAAAAAAAAAZAAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAEAAAABAAAAAAAAkZWR0cwAAABxlbHN0AAAAAAAAAAEAAAGQAAAAAAABAAAAAAHNbWRpYQAAACBtZGhkAAAAAAAAAAAAAAAAAAAoAAAAEABVxAAAAAAALWhkbHIAAAAAAAAAAHZpZGUAAAAAAAAAAAAAAABWaWRlb0hhbmRsZXIAAAABeG1pbmYAAAAUdm1oZAAAAAEAAAAAAAAAAAAAACRkaW5mAAAAHGRyZWYAAAAAAAAAAQAAAAx1cmwgAAAAAQAAAThzdGJsAAAAuHN0c2QAAAAAAAAAAQAAAKhhdmMxAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAEAAQABIAAAASAAAAAAAAAABFExhdmM2MS4zLjEwMCBsaWJ4MjY0AAAAAAAAAAAAAAAAGP//AAAALmF2Y0MBQsAe/+EAF2dCwB7ZBCbARAAAAwAEAAADACg8WLkgAQAEaMuMsgAAABBwYXNwAAAAAQAAAAEAAAAUYnRydAAAAAAAADTkAAA05AAAABhzdHRzAAAAAAAAAAEAAAACAAAIAAAAABRzdHNzAAAAAAAAAAEAAAABAAAAHHN0c2MAAAAAAAAAAQAAAAEAAAACAAAAAQAAABxzdHN6AAAAAAAAAAAAAAACAAACmwAAAAoAAAAUc3RjbwAAAAAAAAABAAADWgAAAGF1ZHRhAAAAWW1ldGEAAAAAAAAAIWhkbHIAAAAAAAAAAG1kaXJhcHBsAAAAAAAAAAAAAAAALGlsc3QAAAAkqXRvbwAAABxkYXRhAAAAAQAAAABMYXZmNjEuMS4xMDAAAAAIZnJlZQAAAq1tZGF0AAACfQYF//953EXpvebZSLeWLNgg2SPu73gyNjQgLSBjb3JlIDE2NCByMzE5MCA3ZWQ3NTNiIC0gSC4yNjQvTVBFRy00IEFWQyBjb2RlYyAtIENvcHlsZWZ0IDIwMDMtMjAyNCAtIGh0dHA6Ly93d3cudmlkZW9sYW4ub3JnL3gyNjQuaHRtbCAtIG9wdGlvbnM6IGNhYmFjPTAgcmVmPTMgZGVibG9jaz0xOjA6MCBhbmFseXNlPTB4MToweDExMSBtZT1oZXggc3VibWU9NyBwc3k9MSBwc3lfcmQ9MS4wMDowLjAwIG1peGVkX3JlZj0xIG1lX3JhbmdlPTE2IGNocm9tYV9tZT0xIHRyZWxsaXM9MSA4eDhkY3Q9MCBjcW09MCBkZWFkem9uZT0yMSwxMSBmYXN0X3Bza2lwPTEgY2hyb21hX3FwX29mZnNldD0tMiB0aHJlYWRzPTIgbG9va2FoZWFkX3RocmVhZHM9MSBzbGljZWRfdGhyZWFkcz0wIG5yPTAgZGVjaW1hdGU9MSBpbnRlcmxhY2VkPTAgYmx1cmF5X2NvbXBhdD0wIGNvbnN0cmFpbmVkX2ludHJhPTAgYmZyYW1lcz0wIHdlaWdodHA9MCBrZXlpbnQ9MjUwIGtleWludF9taW49NSBzY2VuZWN1dD00MCBpbnRyYV9yZWZyZXNoPTAgcmNfbG9va2FoZWFkPTQwIHJjPWFiciBtYnRyZWU9MSBiaXRyYXRlPTggcmF0ZXRvbD0xLjAgcWNvbXA9MC42MCBxcG1pbj0wIHFwbWF4PTY5IHFwc3RlcD00IGlwX3JhdGlvPTEuNDAgYXE9MToxLjAwAIAAAAAWZYiECvJigACa/JycnXXXXXXXXXXXXgAAAAZBmjgT4Rg=';

    var WATCHDOG_MS = 5000;   // how often we re-derive held() while visible
    var ESCALATE_AFTER = 2;   // unexplained drops before we ALSO run the video

    var lock = null;          // WakeLockSentinel when the API path is live
    var video = null;
    var videoWanted = false;  // video is part of the current strategy
    var mode = null;          // last reported verified mode
    /* Who wants the screen. A page is not one caller: /voice-changer has a
       background-mode path and an auto-on-hide path that overlap, /denoise
       holds it across a run while the visibility handler re-takes it, and
       /qr-file-transfer has a sender and a receiver. With a single boolean the
       first subsystem to finish turned the screen off under the others — this
       predates the primitive (they all wrote one shared variable) but a shared
       owner is exactly where it should stop. Holds are counted by name; the
       lock lives while ANY of them does. */
    var holds = Object.create(null);
    var holdCount = 0;
    function wantedNow() { return holdCount > 0; }
    var listeners = [];
    var timer = null;
    var sawHidden = false;    // we have been hidden since the last acquisition
    /* The lock we are about to find missing was lost for a reason we already
       know: the page was hidden, and the spec says a hidden document loses it.
       Measured 2026-08-20: without this, six ordinary tab switches produced
       `drops: 2` and tripped ESCALATE_AFTER — so routine use started the
       fallback video and filed two `wake_lock_dropped` events. That is a
       battery cost for nothing and a telemetry channel that would have
       reported normal behaviour as failure. */
    var expectedLoss = false;
    var drops = 0, repairs = 0, escalated = false, lastError = '';
    var apiError = '';        // why the API refused, kept apart from the video's own error
    var reported = 0;         // telemetry budget for this page
    var lastVideoTime = -1, videoStalls = 0, resumeAttempts = 0;
    var inFlight = null;

    function report(action, extra) {
        if (reported > 12) return;          // a stuck page must not become a firehose
        reported++;
        try {
            if (!window.__ae) return;
            var p = { action: 'wake_lock_' + action, cap: hasApi() ? 'api' : 'none' };
            if (extra) for (var k in extra) if (extra.hasOwnProperty(k)) p[k] = extra[k];
            window.__ae('feature_use', p);
        } catch (e) {}
    }

    function emit(how) {
        for (var i = 0; i < listeners.length; i++) {
            try { listeners[i](how); } catch (e) {}
        }
    }

    function makeVideo() {
        if (video) return video;
        video = document.createElement('video');
        video.setAttribute('muted', '');
        video.muted = true;                    // property AND attribute: iOS wants both
        video.setAttribute('playsinline', ''); // without this iOS goes fullscreen
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('loop', '');
        video.loop = true;
        video.setAttribute('aria-hidden', 'true');
        video.tabIndex = -1;
        // 1px and near-transparent rather than display:none, and inside the
        // viewport rather than the usual left:-9999px parking spot.
        //
        // Be precise about what that buys, because it is less than it looks:
        // placement does NOT affect DECODING. Measured 2026-08-20 in Chromium,
        // all five of {1px visible, left:-9999px, display:none,
        // visibility:hidden, detached from the DOM} reached readyState 4 and
        // kept advancing frames. What we cannot measure from here is whether an
        // OS idle timer counts a video it cannot see as "the user is watching
        // something" — that needs a real phone. So this placement is a cheap
        // precaution against an unverified risk, not a measured requirement.
        // Anyone who gets a device in hand: this is the claim to check.
        video.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;' +
                              'opacity:0.01;pointer-events:none;z-index:-1';
        // <source> elements, not .src: this is what lets the engine pick the
        // codec it actually has instead of failing on the one it doesn't.
        [[SRC_WEBM, 'video/webm'], [SRC_MP4, 'video/mp4']].forEach(function (pair) {
            var srcEl = document.createElement('source');
            srcEl.src = pair[0];
            srcEl.type = pair[1];
            video.appendChild(srcEl);
        });
        // iOS pauses inline video on interruptions (a call, another tab taking
        // audio focus). Resume it — bounded, so a video that can never play
        // does not spin.
        video.addEventListener('pause', function () {
            if (!videoWanted || document.hidden || resumeAttempts > 20) return;
            resumeAttempts++;
            try { var p = video.play(); if (p && p.catch) p.catch(function () {}); } catch (e) {}
        });
        document.body.appendChild(video);
        return video;
    }

    function startVideo() {
        videoWanted = true;
        lastVideoTime = -1; videoStalls = 0;
        try {
            var v = makeVideo();
            var p = v.play();
            if (p && typeof p.then === 'function') {
                return p.then(function () { return 'video'; })
                        .catch(function (e) {
                            // A rejected play() is the whole answer: without it
                            // there is no fallback, and reporting "video" here
                            // is how a dead 1px element got sold as protection.
                            lastError = (e && e.name) || 'play-rejected';
                            videoWanted = false;
                            return 'none';
                        });
            }
            return Promise.resolve('video');
        } catch (e) {
            lastError = 'video-throw';
            videoWanted = false;
            return Promise.resolve('none');
        }
    }

    function stopVideo() {
        videoWanted = false;
        if (!video) return;
        try { video.pause(); } catch (e) {}
    }

    /**
     * `'wakeLock' in navigator` is not enough: the property can exist while
     * being undefined or lacking request() (permissions-policy blocks it in a
     * cross-origin iframe; some vendor builds expose a stub). Then
     * navigator.wakeLock.request() throws a TypeError SYNCHRONOUSLY — before any
     * promise exists — so the caller's .catch() never runs and the video
     * fallback never starts. Duck-type the method, and wrap the call anyway.
     */
    function hasApi() {
        try { return !!(navigator.wakeLock && typeof navigator.wakeLock.request === 'function'); }
        catch (e) { return false; }
    }

    /* -- The truth predicate ---------------------------------------------
       Nothing in this file may ask "do I have an object?". `released` is the
       only thing the spec lets us observe, and a video that is paused, ended,
       undecodable or frozen is not holding anything either. */
    function apiHeld() {
        return !!(lock && lock.released !== true);
    }
    function videoHeld() {
        if (!video || !videoWanted) return false;
        if (video.paused || video.ended) return false;
        if (video.error) return false;
        return video.readyState >= 2;   // HAVE_CURRENT_DATA - actually decoding
    }
    function held() { return apiHeld() || videoHeld(); }

    function verifiedMode() {
        if (apiHeld()) return 'api';
        if (videoHeld()) return 'video';
        return null;
    }

    function syncMode() {
        var m = verifiedMode();
        if (m !== mode) { mode = m; emit(m); }
        return m;
    }

    function requestApi() {
        if (!hasApi()) return Promise.reject(new Error('unsupported'));
        var p;
        try { p = navigator.wakeLock.request('screen'); }
        catch (e) { return Promise.reject(e); }
        if (!p || typeof p.then !== 'function') return Promise.reject(new Error('not a promise'));
        return p.then(function (l) {
            lock = l;
            sawHidden = false;
            l.addEventListener('release', function () {
                // Informational only. The watchdog is what actually repairs -
                // this event is precisely the one bfcache never delivers, so it
                // must not be the only path back.
                if (lock === l) lock = null;
                if (wantedNow() && !document.hidden) schedule(0);
                else syncMode();
            });
            return 'api';
        });
    }

    /** One acquisition attempt. API first, video as fallback (or as escalation). */
    function acquire() {
        if (inFlight) return inFlight;
        inFlight = requestApi().catch(function (e) {
            lastError = (e && (e.name || e.message)) || 'api-failed';
            apiError = lastError;
            // No API, or it refused (some Android builds reject on low battery,
            // iOS on Low Power Mode). Video needs a user gesture on most
            // engines - callers invoke request() from a click, exactly as they
            // must for the API anyway.
            return startVideo();
        }).then(function () {
            if (escalated && apiHeld() && !videoHeld()) startVideo();
            inFlight = null;
            syncMode();
            return verifiedMode() || 'none';
        }, function () {
            inFlight = null;
            syncMode();
            return 'none';
        });
        return inFlight;
    }

    /* -- Watchdog ---------------------------------------------------------
       The part that makes this survive failure modes nobody has named yet: we
       do not need to know WHY the lock went away, only that held() says it is
       gone while the caller still wants it. */
    function check() {
        if (!wantedNow() || document.hidden) return;

        // A video that reports playing but whose clock does not move is not
        // keeping anything awake - engines park undecodable media there.
        if (videoWanted && video && !video.paused) {
            if (video.currentTime === lastVideoTime) {
                if (++videoStalls >= 2) { videoWanted = false; lastError = 'video-stalled'; }
            } else { videoStalls = 0; }
            lastVideoTime = video.currentTime;
        }

        // Consume the flag on every pass: a return-from-hidden that finds the
        // lock still held must not leave it armed to excuse a LATER real drop.
        var expected = expectedLoss;
        expectedLoss = false;

        if (held()) { syncMode(); return; }

        if (!expected) {
            drops++;
            report('dropped', { drops: drops, why: String(lastError || 'silent').slice(0, 40) });
            if (drops >= ESCALATE_AFTER && !escalated) {
                // Something is fighting us and we cannot see what. Add the other
                // lever rather than keep re-taking the one that keeps slipping.
                escalated = true;
                report('escalated', { drops: drops });
                startVideo();
            }
        }
        acquire().then(function (how) {
            // Re-taking after a hide is routine, not a repair — counting it
            // would make the field data unreadable.
            if (how !== 'none' && !expected) { repairs++; report('repaired', { how: how, drops: drops }); }
        });
    }

    function schedule(delay) {
        if (timer) { clearTimeout(timer); timer = null; }
        if (!wantedNow()) return;
        timer = setTimeout(function () { timer = null; check(); schedule(WATCHDOG_MS); },
                           typeof delay === 'number' ? delay : WATCHDOG_MS);
    }

    /* -- Public ----------------------------------------------------------- */

    /** Take a named hold. Returns a handle; the screen stays up until every
     *  hold on the page is released. Taking the same name twice is a no-op, so
     *  a handler that fires repeatedly cannot leak counts. */
    function acquireHold(name) {
        var key = String(name || 'default');
        if (!holds[key]) { holds[key] = true; holdCount++; }
        resumeAttempts = 0;
        schedule(WATCHDOG_MS);
        /* A hidden document cannot hold the screen: the API refuses it by spec
           and Chromium will not play video-only media in a background tab
           (AbortError). Asking anyway was 2/3 of every `wake_lock_failed` on
           the RU shard (30 days to 10.10.2026, /voice-changer alone 1 604
           sessions — it takes a hold as the page goes to the background): a
           failure report and a wasted video for a lock the page could never
           have. The hold stays wanted; the return to the page takes it
           (onReturn), and that loss was expected, not a drop. */
        if (document.hidden) {
            sawHidden = true;
            expectedLoss = true;
            var d = Promise.resolve('deferred');
            d.name = key;
            d.release = function () { releaseHold(key); };
            return d;
        }
        var p = held()
            ? Promise.resolve(syncMode())
            : acquire().then(function (how) {
                  report(how === 'none' ? 'failed' : 'acquired',
                         { how: how, err: how === 'none' ? String(lastError).slice(0, 60) : undefined,
                           api: how === 'none' ? String(apiError).slice(0, 60) : undefined });
                  return how;
              });
        p.name = key;
        p.release = function () { releaseHold(key); };
        return p;
    }

    /** Drop one hold. The screen only goes back to the OS when it was the last. */
    function releaseHold(name) {
        var key = String(name || 'default');
        if (!holds[key]) return;
        delete holds[key];
        holdCount--;
        if (holdCount > 0) return;          // somebody else still needs it
        teardown();
    }

    /** Hard stop regardless of who is holding — page teardown, not normal use. */
    function releaseAll() {
        holds = Object.create(null);
        holdCount = 0;
        teardown();
    }

    function teardown() {
        escalated = false;
        if (timer) { clearTimeout(timer); timer = null; }
        if (lock) { try { lock.release(); } catch (e) {} lock = null; }
        stopVideo();
        mode = null;
        emit(null);
    }

    /* The pre-hold API, kept because ~20 call sites use it and it is the right
       shape for a tool with ONE thing that needs the screen: it is the hold
       named 'default'. A tool with two subsystems names them instead. */
    function request() { return acquireHold('default'); }
    function release() { releaseHold('default'); }

    /* -- Lifecycle re-arm --------------------------------------------------
       Per spec a hidden document loses its screen lock, so on the way back the
       old sentinel is dead by definition - we drop it instead of waiting for
       the `release` event, which a frozen/bfcached page never delivers. */
    function onReturn() {
        if (!wantedNow()) return;
        if (sawHidden) { lock = null; sawHidden = false; expectedLoss = true; }
        schedule(0);
    }
    document.addEventListener('visibilitychange', function () {
        if (document.hidden) { sawHidden = true; return; }
        onReturn();
    });
    // iOS Safari is the reason this is not visibilitychange alone: the codebase
    // already relies on pagehide there because visibilitychange is unreliable
    // (telemetry-tier0.js says so at its own flush site). A persisted pageshow
    // is a restore from bfcache, where nothing else fires at all.
    window.addEventListener('pageshow', function (e) {
        if (e && e.persisted) sawHidden = true;
        onReturn();
    });
    window.addEventListener('focus', function () { if (sawHidden) onReturn(); });
    document.addEventListener('resume', onReturn);   // Page Lifecycle API

    window.__wakeLock = {
        request: request,
        release: release,
        /** Named, refcounted hold for a page with more than one such subsystem. */
        hold: acquireHold,
        releaseHold: releaseHold,
        releaseAll: releaseAll,
        /** VERIFIED mode - 'api' | 'video' | null. Never "what we asked for". */
        active: function () { return verifiedMode(); },
        /** 'api' if the browser has the real thing, 'video' if we'd fall back. */
        capability: function () { return hasApi() ? 'api' : 'video'; },
        /** Everything a caller needs to tell the user the truth. */
        state: function () {
            return {
                mode: verifiedMode(), held: held(), wanted: wantedNow(),
                holds: Object.keys(holds),
                drops: drops, repairs: repairs, escalated: escalated,
                lastError: String(lastError || '')
            };
        },
        onChange: function (fn) { if (typeof fn === 'function') listeners.push(fn); },
        /** Test seam: force one watchdog pass now. */
        _check: check,
        version: '2.3.0'
    };
})();
